import {
  TASK_STATUS_BADGE_CLASSES,
  TASK_STATUS_DOT_CLASSES,
  TASK_STATUS_LABELS,
  type TaskStatus,
} from '../../lib/taskStatus';
import {
  TASK_PRIORITY_DOT_CLASSES,
  TASK_PRIORITY_LABELS,
  type TaskPriority,
} from '../../lib/taskPriority';
import { Badge } from '../ui';

export function TaskStatusBadge({ status }: { status: TaskStatus }) {
  return <Badge className={TASK_STATUS_BADGE_CLASSES[status]}>{TASK_STATUS_LABELS[status]}</Badge>;
}

/**
 * The status as a dot, for a list row that has no room for the pill.
 *
 * Shaped like PriorityDot rather than derived from it: the two read from
 * different maps and the name has to be spelled differently to screen readers
 * ("To do status" against "High priority"), which is most of what either of
 * them does.
 *
 * The colour is the only thing on the row that says which state a task is in,
 * so the name is on the `title` as well -- colour alone is not a label.
 */
export function StatusDot({ status }: { status: TaskStatus }) {
  return (
    <span className="inline-flex items-center" title={`${TASK_STATUS_LABELS[status]} status`}>
      <span
        className={`h-2 w-2 shrink-0 rounded-full ${TASK_STATUS_DOT_CLASSES[status]}`}
        aria-hidden="true"
      />
      <span className="sr-only">{TASK_STATUS_LABELS[status]} status</span>
    </span>
  );
}

/** A dot rather than a second pill, so a row does not carry two competing
 *  colour chips. `label` spells it out where there is room for it. */
export function PriorityDot({ priority, label = false }: { priority: TaskPriority; label?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1 text-meta text-stone-600">
      <span
        className={`h-2 w-2 shrink-0 rounded-full ${TASK_PRIORITY_DOT_CLASSES[priority]}`}
        aria-hidden="true"
      />
      <span className={label ? 'truncate' : 'sr-only'}>{TASK_PRIORITY_LABELS[priority]}</span>
      {/* Kept off the visible label so it fits a column, but still read out. */}
      <span className="sr-only">priority</span>
    </span>
  );
}

/**
 * Takes a label rather than a person, because the two kinds of person here do
 * not share a shape: an assignee has a required name and an optional email and
 * never a picture, while a signed-in user is the other way round. Each caller
 * already knows which it is holding, so it says so.
 */
export function Avatar({
  label,
  pictureUrl = null,
  className = 'h-6 w-6',
}: {
  label: string | null;
  pictureUrl?: string | null;
  className?: string;
}) {
  if (!label) return null;

  // referrerPolicy matches Layout's user block: Google's avatar host 403s some
  // requests that carry a referrer.
  return pictureUrl ? (
    <img
      src={pictureUrl}
      alt={label}
      title={label}
      referrerPolicy="no-referrer"
      className={`shrink-0 rounded-full ${className}`}
    />
  ) : (
    <span
      title={label}
      className={`inline-flex shrink-0 items-center justify-center rounded-full bg-stone-200 text-micro font-medium uppercase text-stone-600 ${className}`}
    >
      {initialsOf(label)}
    </span>
  );
}

/**
 * First letters of the first two words, not the first two characters.
 *
 * Assignee labels are full human names now rather than email locals, and
 * `slice(0, 2)` renders an identical "AH" for everyone who shares a first name
 * -- which on a hand-curated list of colleagues is the common case, not the
 * edge case.
 */
function initialsOf(label: string): string {
  return label
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0] ?? '')
    .join('')
    .toUpperCase();
}

/**
 * Everyone on a task, owner first, as overlapping initials.
 *
 * Capped at three with a `+N` counter: the column is a fixed track shared with
 * every other row, and an uncapped stack would either overflow it or force it
 * wide enough for the worst case on every row that does not need it.
 */
export function AvatarStack({
  people,
  max = 3,
}: {
  people: { id: string; name: string }[];
  max?: number;
}) {
  if (people.length === 0) return null;

  const shown = people.slice(0, max);
  const hidden = people.length - shown.length;

  return (
    // Negative spacing overlaps them; ring-white keeps each one readable against
    // the one behind it.
    <span className="flex -space-x-1.5" title={people.map((person) => person.name).join(', ')}>
      {shown.map((person) => (
        <Avatar key={person.id} label={person.name} className="h-5 w-5 ring-2 ring-white" />
      ))}
      {hidden > 0 ? (
        <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-stone-100 text-micro font-medium text-stone-500 ring-2 ring-white">
          +{hidden}
        </span>
      ) : null}
    </span>
  );
}
